// ========== API CONFIGURATION ==========
const DEV_API_BASE = "http://localhost:8080/api";
const STAGING_API_BASE = "https://spring-dev-yphs.onrender.com/api";
const DEV_SERVER_BASE = "http://localhost:8080";
const STAGING_SERVER_BASE = "https://spring-dev-yphs.onrender.com";

const isBrowserLocalhost = () => {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host === "localhost" || host === "127.0.0.1";
};

const resolveApiBase = () => {
  const envUrl = process.env.REACT_APP_API_BASE_URL;
  if (isBrowserLocalhost()) {
    return envUrl || DEV_API_BASE;
  }
  // Deployed sites (dev.aminii.in, Netlify, etc.) must never call localhost
  if (envUrl && !envUrl.includes("localhost") && !envUrl.includes("127.0.0.1")) {
    return envUrl;
  }
  return STAGING_API_BASE;
};

const resolveServerBase = () => {
  const envUrl = process.env.REACT_APP_SERVER_BASE_URL;
  if (isBrowserLocalhost()) {
    return envUrl || DEV_SERVER_BASE;
  }
  if (envUrl && !envUrl.includes("localhost") && !envUrl.includes("127.0.0.1")) {
    return envUrl;
  }
  return STAGING_SERVER_BASE;
};

export const API_BASE_URL = resolveApiBase();
export const SERVER_BASE_URL = resolveServerBase();
export const WS_BASE_URL =
  process.env.REACT_APP_WS_BASE_URL &&
  !process.env.REACT_APP_WS_BASE_URL.includes("localhost")
    ? process.env.REACT_APP_WS_BASE_URL
    : SERVER_BASE_URL;

export const API_CONFIG = {
  baseURL: API_BASE_URL,
  timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || "10000"),
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
};

// ========== TOKEN CONFIGURATION ==========
export const TOKEN_CONFIG = {
  accessTokenKey: "accessToken",
  refreshTokenKey: "refreshToken",
  userIdKey: "userId",
  userFirstNameKey: "userFirstName",
  userRoleKey: "userRole",
  accessTokenLifetime: 60 * 60 * 1000, // 1 hour in milliseconds
  refreshTokenLifetime: 30 * 24 * 60 * 60 * 1000, // 30 days
};

// ========== SESSION CONFIGURATION ==========
export const SESSION_CONFIG = {
  inactivityTimeout: 15 * 60 * 1000, // 15 minutes
  warningTime: 2 * 60 * 1000, // Warn at 13 minutes (15 - 2)
  checkInterval: 60 * 1000, // Check every 1 minute
};

// ========== ERROR MESSAGES ==========
export const ERROR_MESSAGES = {
  // Generic errors (shown to user)
  GENERIC_ERROR: "Something went wrong. Please try again later.",
  NETWORK_ERROR: "Network error. Please check your connection.",
  TIMEOUT_ERROR: "Request timeout. Please try again.",

  // Auth errors
  UNAUTHORIZED: "Session expired. Please log in again.",
  FORBIDDEN: "Access denied.",
  INVALID_CREDENTIALS: "Invalid email or password.",

  // Rate limiting
  TOO_MANY_ATTEMPTS: "Too many attempts. Try again in 15 minutes.",
  TOO_MANY_LOGIN_ATTEMPTS: "Too many login attempts. Try again in 15 minutes.",
  TOO_MANY_OTP_ATTEMPTS: "Too many OTP attempts. Try again in 15 minutes.",

  // Validation errors
  INVALID_EMAIL: "Please enter a valid email address.",
  INVALID_PASSWORD:
    "Password must be 8-25 characters with 1 number and 1 special character.",
  PASSWORDS_DONT_MATCH: "Passwords do not match.",
  INVALID_PHONE: "Please enter a valid 10-digit phone number.",
  INVALID_OTP: "Please enter a valid 6-digit OTP.",

  // Form errors
  REQUIRED_FIELD: "This field is required.",
  INVALID_URL: "Please enter a valid URL.",
  INVALID_PRICE: "Please enter a valid price.",

  // Login/Register errors
  EMAIL_ALREADY_EXISTS: "Email already registered.",
  USER_NOT_FOUND: "User not found.",
  OTP_EXPIRED: "OTP has expired. Please request a new one.",

  // Cart/Order errors
  EMPTY_CART: "Your cart is empty.",
  INVALID_ADDRESS: "Please provide a valid shipping address.",
  INVALID_PAYMENT_METHOD: "Please select a payment method.",

  // File upload errors
  INVALID_FILE_TYPE: "Invalid file type.",
  FILE_TOO_LARGE: "File size exceeds maximum limit.",
  INVALID_IMAGE_URL: "Invalid image URL.",
};

// ========== SUCCESS MESSAGES ==========
export const SUCCESS_MESSAGES = {
  LOGIN_SUCCESS: "Logged in successfully!",
  REGISTER_SUCCESS: "Registration successful! Please verify your email.",
  PASSWORD_RESET_SUCCESS: "Password reset successful. Please log in.",
  LOGOUT_SUCCESS: "Logged out successfully.",
  PROFILE_UPDATE_SUCCESS: "Profile updated successfully.",
  ORDER_PLACED: "Order placed successfully!",
  PAYMENT_SUCCESSFUL: "Payment successful!",
  PRODUCT_ADDED: "Product added to cart.",
  PRODUCT_REMOVED: "Product removed from cart.",
  ADDRESS_SAVED: "Address saved successfully.",
};

// ========== VALIDATION PATTERNS ==========
export const VALIDATION_PATTERNS = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PASSWORD: /^(?=.*[0-9])(?=.*[!@#$%^&*])[A-Za-z0-9!@#$%^&*]{8,25}$/,
  PHONE: /^[6-9][0-9]{9}$/, // 10-digit Indian phone number
  OTP: /^\d{6}$/, // 6-digit OTP
  ZIP_CODE: /^\d{6}$/, // 6-digit Indian zip code
  URL: /^https?:\/\/[^\s/$.?#].[^\s]*$/i,
  PRODUCT_NAME: /^[A-Za-z0-9\s\-_]{3,100}$/,
};

// ========== API ENDPOINTS ==========
export const API_ENDPOINTS = {
  // Auth
  AUTH: {
    LOGIN: "/login",
    REGISTER: "/auth/register",
    VERIFY_OTP: "/auth/verify-otp",
    RESEND_OTP: "/auth/resend-otp",
    FORGOT_PASSWORD: "/forgot-password",
    REFRESH_TOKEN: "/refresh",
    LOGOUT: "/logout",
  },

  // User
  USER: {
    GET_CURRENT: "/users/me",
    GET_BY_ID: (id) => `/users/${id}`,
    UPDATE_PROFILE: (id) => `/users/${id}`,
    CHANGE_PASSWORD: "/users/change-password",
  },

  // User Details (Addresses)
  USER_DETAILS: {
    GET_ALL: "/userdetails",
    GET_BY_USER: (userId) => `/userdetails/user/${userId}`,
    CREATE: "/userdetails",
    UPDATE: (id) => `/userdetails/${id}`,
    DELETE: (id) => `/userdetails/${id}`,
  },

  // Products
  PRODUCTS: {
    GET_ALL: "/products",
    GET_BY_ID: (id) => `/products/${id}`,
    GET_BY_CATEGORY: (category) => `/products/${category}`,
    GET_BY_CATEGORY_SUBCATEGORY: (category, subcategory) =>
      `/products/${category}/${subcategory}`,
    SEARCH: (query) => `/products/search?q=${encodeURIComponent(query)}`,
    CREATE: "/all-product-curd",
    UPDATE: (id) => `/products/${id}`,
    DELETE: (id) => `/products/${id}`,
  },

  // Cart
  CART: {
    GET_BY_USER: (userId) => `/cart/user/${userId}`,
    ADD: "/cart",
    UPDATE: (cartId) => `/cart/${cartId}`,
    DELETE: (cartId) => `/cart/${cartId}`,
    CLEAR: "/cart/user",
  },

  // Orders
  ORDERS: {
    GET_ALL: "/orders",
    GET_BY_USER: (userId) => `/orders/user/${userId}`,
    GET_BY_ID: (orderId) => `/orders/${orderId}`,
    CREATE: "/orders",
    UPDATE: (orderId) => `/orders/${orderId}`,
    CANCEL: (orderId) => `/orders/${orderId}/cancel`,
  },

  // Payment
  PAYMENT: {
    VERIFY: "/verify-payment",
    GET_RAZORPAY_KEY: "/razorpay-key",
    PAYMENT_FAILED: "/payment-failed",
  },

  // CSRF
  CSRF: {
    GET_TOKEN: "/csrf-token",
  },
};

// ========== HTTP STATUS CODES ==========
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  RATE_LIMITED: 429,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
};

// ========== USER ROLES ==========
export const USER_ROLES = {
  ADMIN: "Aminiiadmin",
  USER: "USER",
  GUEST: "GUEST",
};

// ========== CATEGORIES ==========
export const CATEGORIES = {
  girl: {
    name: "Girl",
    subcategories: ["Cotton Wear", "Party Frocks", "Premium Ethnic", "Western"],
  },
  women: {
    name: "Women",
    subcategories: ["Luxe Cordset", "Premium Pakistani", "Jeans"],
  },
};

// ========== PAYMENT METHODS ==========
export const PAYMENT_METHODS = {
  ONLINE_PAYMENT: "ONLINE_PAYMENT",
  UPI: "UPI",
  COD: "COD",
};

// ========== ORDER STATUS ==========
export const ORDER_STATUS = {
  PENDING: "PENDING",
  RECEIVED: "RECEIVED",
  SHIPPED: "SHIPPED",
  OUT_FOR_DELIVERY: "OUT_FOR_DELIVERY",
  DELIVERED: "DELIVERED",
  CANCELLED: "CANCELLED",
};

// ========== LOCAL STORAGE KEYS ==========
export const STORAGE_KEYS = {
  TOKEN: "token",
  REFRESH_TOKEN: "refreshToken",
  USER_ID: "userId",
  USER_FIRST_NAME: "userFirstName",
  USER_ROLE: "userRole",
  IS_LOGGED_IN: "isLoggedIn",
  LOGOUT_REASON: "logoutReason",
};

export default {
  API_CONFIG,
  TOKEN_CONFIG,
  SESSION_CONFIG,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  VALIDATION_PATTERNS,
  API_ENDPOINTS,
  HTTP_STATUS,
  USER_ROLES,
  CATEGORIES,
  PAYMENT_METHODS,
  ORDER_STATUS,
  STORAGE_KEYS,
};
